import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, PhoneOff, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Notification sound - using a simple beep pattern
const playAlertSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playBeep = (startTime, frequency = 800, duration = 0.15) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.5, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    // Play a distinctive double-beep pattern
    const now = audioContext.currentTime;
    playBeep(now, 880, 0.15);
    playBeep(now + 0.2, 880, 0.15);
    playBeep(now + 0.5, 1100, 0.2);
    
  } catch (error) {
    console.warn("Could not play alert sound:", error);
  }
};

// Request browser notification permission
const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("Browser does not support notifications");
    return false;
  }
  
  if (Notification.permission === "granted") {
    return true;
  }
  
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  
  return false;
};

// Show browser notification
const showBrowserNotification = (title, body) => {
  if (Notification.permission === "granted") {
    const notification = new Notification(title, {
      body: body,
      icon: "/favicon.ico",
      tag: "cashier-alert",
      requireInteraction: true,
      silent: false
    });
    
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    return notification;
  }
  return null;
};

export const ManagerAlertListener = ({ isAdmin }) => {
  const [activeAlert, setActiveAlert] = useState(null);
  const [showInlineAlert, setShowInlineAlert] = useState(false);
  const soundIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const notificationRef = useRef(null);
  const isDismissedRef = useRef(false); // Flag to immediately stop sounds
  
  // Stop all sounds and notifications immediately
  const stopAllAlerts = useCallback(() => {
    isDismissedRef.current = true;
    
    // Stop the sound interval
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    
    // Close browser notification
    if (notificationRef.current) {
      notificationRef.current.close();
      notificationRef.current = null;
    }
  }, []);
  
  // Dismiss the alert
  const dismissAlert = useCallback(async () => {
    // Immediately stop sounds before API call
    stopAllAlerts();
    setActiveAlert(null);
    setShowInlineAlert(false);
    
    try {
      await axios.post(`${API}/alerts/dismiss`);
      toast.success("Alert dismissed");
    } catch (error) {
      console.error("Error dismissing alert:", error);
    }
  }, [stopAllAlerts]);
  
  // Check for active alerts
  const checkForAlerts = useCallback(async () => {
    // Don't check if recently dismissed
    if (isDismissedRef.current) return;
    
    try {
      const response = await axios.get(`${API}/alerts/active`);
      const alert = response.data.alert;
      
      if (alert && !alert.dismissed) {
        // New alert detected
        if (!activeAlert || activeAlert.id !== alert.id) {
          isDismissedRef.current = false; // Reset dismissed flag for new alert
          setActiveAlert(alert);
          setShowInlineAlert(true);
          
          // Play initial sound
          playAlertSound();
          
          // Show browser notification
          notificationRef.current = showBrowserNotification(
            "🔔 Cashier Needs Help!",
            alert.message || "Manager needed at register"
          );
          
          // Start repeating sound every 5 seconds
          if (soundIntervalRef.current) {
            clearInterval(soundIntervalRef.current);
          }
          soundIntervalRef.current = setInterval(() => {
            // Check dismissed flag before playing
            if (!isDismissedRef.current) {
              playAlertSound();
            }
          }, 5000);
          
          // Show toast
          toast.warning("Cashier is calling for help!", {
            duration: 10000,
            action: {
              label: "Dismiss",
              onClick: dismissAlert
            }
          });
        }
      } else {
        // No active alert - clear everything
        if (activeAlert) {
          stopAllAlerts();
          setActiveAlert(null);
          setShowInlineAlert(false);
        }
      }
    } catch (error) {
      // Silently fail - don't spam console
    }
  }, [activeAlert, dismissAlert, stopAllAlerts]);
  
  // Request notification permission on mount
  useEffect(() => {
    if (isAdmin) {
      requestNotificationPermission();
    }
  }, [isAdmin]);
  
  // Start polling for alerts (only for admin users)
  useEffect(() => {
    if (!isAdmin) return;
    
    // Initial check with small delay to avoid synchronous setState
    const initialCheckTimeout = setTimeout(checkForAlerts, 100);
    
    // Poll every 3 seconds
    pollIntervalRef.current = setInterval(checkForAlerts, 3000);
    
    return () => {
      clearTimeout(initialCheckTimeout);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
      if (notificationRef.current) {
        notificationRef.current.close();
      }
    };
  }, [isAdmin, checkForAlerts]);
  
  // Don't render anything if not admin or no alert
  if (!isAdmin || !showInlineAlert) return null;
  
  return (
    <div className="fixed top-4 right-4 z-[9999] animate-pulse">
      <div className="bg-red-600 text-white p-4 rounded-lg shadow-2xl border-4 border-red-400 max-w-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-white/20 rounded-full animate-bounce">
            <Phone className="h-6 w-6" />
          </div>
          <div>
            <div className="font-bold text-lg">Cashier Calling!</div>
            <div className="text-sm text-red-100">
              {activeAlert?.message || "Manager needed at register"}
            </div>
          </div>
        </div>
        <Button
          onClick={dismissAlert}
          className="w-full bg-white text-red-600 hover:bg-red-100 font-bold"
        >
          <PhoneOff className="h-4 w-4 mr-2" />
          Dismiss Alert
        </Button>
      </div>
    </div>
  );
};

export default ManagerAlertListener;
